import { useState } from 'react';
import { Popover } from '@mui/material';

import { ListFilterOption } from '@common/ui/ListFilters/common';
import ListFilterSelectChipTrigger from '@common/ui/ListFilters/ListFilterSelectChipTrigger';
import ListFilterSingleSelect from '@common/ui/ListFilters/ListFilterSingleSelect';

export const ASSESSMENT_REPORT_BOOLEAN_FILTER_OPTION = {
  yes: 'yes',
  no: 'no',
} as const;

interface AssessmentReportBooleanFilterProps {
  label: string;
  selected: string[];
  onSelect: (value: string[]) => void;
}

function useOptions({ selected }: Pick<AssessmentReportBooleanFilterProps, 'selected'>) {
  const options: ListFilterOption[] = [
    {
      value: ASSESSMENT_REPORT_BOOLEAN_FILTER_OPTION.yes,
      label: 'Yes',
    },
    {
      value: ASSESSMENT_REPORT_BOOLEAN_FILTER_OPTION.no,
      label: 'No',
    },
  ];

  const selectedOption = options.find((o) => selected.includes(o.value));

  return {
    options,
    selectedOption,
  };
}

function useOpenClose() {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | undefined>(undefined);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(undefined);
  };

  return { opened: !!anchorEl, anchorEl, handleOpen, handleClose };
}

function useSelectHandler({ onSelect }: Pick<AssessmentReportBooleanFilterProps, 'onSelect'>) {
  const onSelectHandler = (value: ListFilterOption) => {
    onSelect([value.value]);
  };

  const onClear = () => {
    onSelect([]);
  };

  return { onSelectHandler, onClear };
}

export default function AssessmentReportBooleanFilter({
  label,
  selected,
  onSelect,
}: AssessmentReportBooleanFilterProps) {
  const { onSelectHandler, onClear } = useSelectHandler({ onSelect });
  const { opened, anchorEl, handleOpen, handleClose } = useOpenClose();
  const { options, selectedOption } = useOptions({ selected });

  return (
    <>
      <ListFilterSelectChipTrigger
        active={opened}
        label={label}
        value={selectedOption ? [selectedOption] : []}
        onClear={onClear}
        onClick={(e) => {
          handleOpen(e);
        }}
      />
      <Popover
        open={opened}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <ListFilterSingleSelect
          width={300}
          options={options}
          value={selectedOption}
          onSelect={onSelectHandler}
          title={`Select ${label} Filter`}
        />
      </Popover>
    </>
  );
}
